"use client";

import { useEffect, useMemo, useState, type FC } from "react";
import {
    AlertCircle,
    AlertTriangle,
    BookOpen,
    Brain,
    Check,
    ChevronRight,
    ClipboardCheck,
    ClipboardList,
    Factory,
    FileChartColumn,
    FileSearch,
    FileText,
    FolderOpen,
    GitCompare,
    LayoutList,
    Library,
    ListChecks,
    ListFilter,
    ListPlus,
    Loader2,
    Newspaper,
    PenLine,
    ScanSearch,
    Search,
    ShieldCheck,
    Sparkles,
    Tag,
    Wand2,
    XCircle,
} from "lucide-react";
import { cn } from "@/lib/utils";
import {
    Collapsible,
    CollapsibleContent,
    CollapsibleTrigger,
} from "@/components/ui/collapsible";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type StepStatus = "in_progress" | "complete" | "error" | "interrupted";

export interface ThinkingStep {
    type: "thinking";
    text: string;
    status: StepStatus;
    durationMs?: number;
}

export interface ToolStep {
    type: "tool";
    toolName: string;
    toolCallId: string;
    status: StepStatus;
    durationMs?: number;
    args?: Record<string, unknown>;
    /** Human-readable loading text from backend (e.g. "Searching documents...") */
    loadingText?: string;
    /** Human-readable result summary from backend */
    resultSummary?: string;
    /** Raw result object (e.g. { data: [...], total_results: N }) */
    result?: unknown;
    error?: string;
}

export interface ProcessingStep {
    type: "processing";
    name: string;
    status: StepStatus;
    summaryText?: string;
    durationMs?: number;
}

export type ChainStep = ThinkingStep | ToolStep | ProcessingStep;

export interface ChainOfThoughtProps {
    steps: ChainStep[];
    isStreaming?: boolean;
    defaultOpen?: boolean;
}

// ---------------------------------------------------------------------------
// useElapsedTime: live timer for in-progress states
// ---------------------------------------------------------------------------

function useElapsedTime(isActive: boolean): number {
    const [elapsed, setElapsed] = useState(0);
    const [startTime] = useState(() => Date.now());

    useEffect(() => {
        if (!isActive) return;

        const interval = setInterval(() => {
            setElapsed(Math.floor((Date.now() - startTime) / 100) / 10);
        }, 100);

        return () => clearInterval(interval);
    }, [isActive, startTime]);

    return elapsed;
}

/**
 * Format a step's duration for display. Shows live elapsed time while
 * in progress, and the final backend duration once complete/errored.
 */
function formatStepDuration(
    status: StepStatus,
    durationMs: number | undefined,
    elapsedTime: number
): string | null {
    if (status === "in_progress") {
        return elapsedTime.toFixed(1);
    }
    if (
        status === "complete" ||
        status === "error" ||
        status === "interrupted"
    ) {
        return durationMs != null && durationMs > 0
            ? (durationMs / 1000).toFixed(1)
            : elapsedTime.toFixed(1);
    }
    return null;
}

// ---------------------------------------------------------------------------
// StatusIndicator
// ---------------------------------------------------------------------------

const StatusIndicator: FC<{ status?: StepStatus }> = ({ status }) => {
    if (status === "in_progress") {
        return (
            <span className="text-primary flex size-4 shrink-0 animate-spin items-center justify-center">
                <Loader2 size={12} />
            </span>
        );
    }

    if (status === "error") {
        return (
            <span className="flex size-4 shrink-0 items-center justify-center">
                <XCircle size={16} className="text-destructive" />
            </span>
        );
    }

    if (status === "interrupted") {
        return (
            <span className="bg-muted-foreground/30 flex size-4 shrink-0 items-center justify-center rounded-full" />
        );
    }

    return (
        <span className="flex size-4 shrink-0 items-center justify-center rounded-full bg-emerald-600">
            <Check size={10} className="text-white" strokeWidth={3} />
        </span>
    );
};

// ---------------------------------------------------------------------------
// Tool icon map
// ---------------------------------------------------------------------------

// Keyed by agent tool name; the backing registry is
// api/app/services/v1/agent_chat_service/tools/registry.py. An unmapped name
// falls back to Sparkles, which also covers tool names renamed since a stored
// conversation was written.
const TOOL_ICONS: Record<string, FC<{ size?: number }>> = {
    document_search: FileSearch,
    get_scope: FileText,
    find_scopes: Search,
    find_certificates: ShieldCheck,
    scopes_for_clause: Search,
    list_folders: FolderOpen,
    list_comparisons: GitCompare,
    comparison_detail: GitCompare,
    checklist_detail: ClipboardCheck,
    list_checklists: ClipboardList,
    topic_detail: BookOpen,
    clause_detail: FileText,
    list_clauses: ListFilter,
    list_policy_types: Tag,
    list_section_types: LayoutList,
    list_industries: Factory,
    industry_detail: Factory,
    industry_report: Newspaper,
    comparisons_for_scopes: GitCompare,
    comparisons_for_checklist: GitCompare,
    knowledge_base_search: Library,
    find_documents: FileSearch,
    get_document: FileText,
    get_page_content: FileText,
    get_review_context: ScanSearch,
    get_expected_fields: ListChecks,
    update_document_field: PenLine,
    create_custom_field: ListPlus,
    generate_report: FileChartColumn,
};

// ---------------------------------------------------------------------------
// ThinkingRow
// ---------------------------------------------------------------------------

const ThinkingRow: FC<{
    step: ThinkingStep;
    isLast?: boolean;
}> = ({ step, isLast }) => {
    const [expanded, setExpanded] = useState(false);
    const elapsedTime = useElapsedTime(step.status === "in_progress");
    const displayDuration = formatStepDuration(
        step.status,
        step.durationMs,
        elapsedTime
    );

    return (
        <div
            className={cn(
                "animate-in fade-in slide-in-from-top-1 duration-200",
                !isLast && "mb-3"
            )}
        >
            <div
                onClick={() => setExpanded(!expanded)}
                className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 transition-colors hover:bg-black/[0.03]"
            >
                <StatusIndicator status={step.status} />

                <Brain size={14} className="text-[#223876] opacity-70" />

                <span className="text-foreground text-[0.8125rem] font-medium">
                    Reasoning
                </span>

                {displayDuration && (
                    <span className="text-muted-foreground ml-1 text-xs tabular-nums">
                        {displayDuration}s
                    </span>
                )}

                <span className="flex-1" />

                {step.text && (
                    <ChevronRight
                        size={14}
                        className={cn(
                            "text-[#223876] opacity-50 transition-transform duration-200",
                            expanded && "rotate-90"
                        )}
                    />
                )}
            </div>

            {expanded && step.text && (
                <div className="mt-1 ml-7 rounded-md border-l-[3px] border-[rgba(34,56,118,0.2)] bg-black/[0.02] p-3">
                    <p className="text-muted-foreground text-[0.8125rem] leading-relaxed whitespace-pre-wrap italic">
                        {step.text}
                    </p>
                </div>
            )}
        </div>
    );
};

// ---------------------------------------------------------------------------
// ToolRow
// ---------------------------------------------------------------------------

interface SearchResult {
    text: string;
    page?: number;
    relevance: number | null;
}

const ToolRowExpanded: FC<{
    step: ToolStep;
    truncatedQuery: string | null;
}> = ({ step, truncatedQuery }) => {
    const isError = step.status === "error";

    let results: SearchResult[] | null = null;
    if (step.toolName === "document_search" && step.result) {
        const r = step.result as
            | {
                  data?: Array<{
                      text?: string;
                      page_number?: number;
                      score?: number;
                  }>;
              }
            | undefined;
        if (r?.data) {
            results = r.data.slice(0, 5).map((item) => ({
                text:
                    (item.text?.substring(0, 200) ?? "") +
                    (item.text && item.text.length > 200 ? "..." : ""),
                page: item.page_number,
                relevance: item.score ? Math.round(item.score * 100) : null,
            }));
        }
    }

    return (
        <div className="mt-1 ml-7 rounded-md border-l-[3px] border-[#223876] bg-black/[0.02] p-3 opacity-80">
            {truncatedQuery && (
                <div className={cn(results && results.length > 0 && "mb-4")}>
                    <span className="text-muted-foreground text-[0.6875rem] font-semibold tracking-wider uppercase">
                        Search Query
                    </span>
                    <p className="text-foreground mt-1 text-[0.8125rem] italic">
                        "{truncatedQuery}"
                    </p>
                </div>
            )}

            {results && results.length > 0 && (
                <div>
                    <span className="text-muted-foreground text-[0.6875rem] font-semibold tracking-wider uppercase">
                        Top Results
                    </span>
                    <div className="mt-2">
                        {results.map((result, idx) => (
                            <div
                                key={idx}
                                className={cn(
                                    "py-2",
                                    idx < results.length - 1 &&
                                        "border-border border-b"
                                )}
                            >
                                <div className="mb-1 flex items-center gap-2">
                                    {result.page && (
                                        <span className="rounded bg-[rgba(34,56,118,0.08)] px-1.5 py-0.5 text-[0.6875rem] font-medium">
                                            Page {result.page}
                                        </span>
                                    )}
                                    {result.relevance && (
                                        <span className="text-muted-foreground text-[0.6875rem]">
                                            {result.relevance}% match
                                        </span>
                                    )}
                                </div>
                                <p className="text-muted-foreground text-[0.75rem] leading-relaxed">
                                    {result.text}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {isError && step.error && (
                <p className="text-destructive text-[0.8125rem]">
                    {step.error}
                </p>
            )}
        </div>
    );
};

const ToolRow: FC<{
    step: ToolStep;
    isLast?: boolean;
}> = ({ step, isLast }) => {
    const [expanded, setExpanded] = useState(false);
    const isComplete = step.status === "complete";
    const isError = step.status === "error";
    const isInProgress = step.status === "in_progress";
    const elapsedTime = useElapsedTime(isInProgress);
    const displayDuration = formatStepDuration(
        step.status,
        step.durationMs,
        elapsedTime
    );

    const loadingText = step.loadingText || `Running ${step.toolName}...`;
    const completeSummary = step.resultSummary || "";

    let summary: string;
    if (isError) {
        summary = completeSummary || "An error occurred";
    } else if (isComplete) {
        summary = completeSummary;
    } else {
        summary = loadingText;
    }

    const label = useMemo(
        () =>
            step.toolName
                .split("_")
                .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
                .join(" "),
        [step.toolName]
    );

    const query = (step.args?.query as string) || null;
    const truncatedQuery =
        query && query.length > 50 ? query.substring(0, 50) + "..." : query;

    const ToolIcon = TOOL_ICONS[step.toolName] ?? Sparkles;

    const hasExpandableContent =
        isComplete && (step.toolName === "document_search" || truncatedQuery);
    const handleClick = hasExpandableContent
        ? () => setExpanded(!expanded)
        : undefined;

    return (
        <div
            className={cn(
                "animate-in fade-in slide-in-from-top-1 duration-200",
                !isLast && "mb-3"
            )}
        >
            <div
                onClick={handleClick}
                className={cn(
                    "flex items-center gap-2 rounded-md px-2 py-1.5 transition-colors",
                    hasExpandableContent
                        ? "cursor-pointer hover:bg-black/[0.03]"
                        : "cursor-default"
                )}
            >
                <StatusIndicator status={step.status} />

                <span className="flex shrink-0 text-[#223876] opacity-70">
                    <ToolIcon size={14} />
                </span>

                <span
                    className={cn(
                        "min-w-0 flex-1 truncate text-[0.8125rem] font-medium",
                        isError ? "text-destructive" : "text-foreground"
                    )}
                >
                    {summary || label}
                </span>

                {displayDuration && (
                    <span className="text-muted-foreground shrink-0 text-xs tabular-nums">
                        ({displayDuration}s)
                    </span>
                )}

                {hasExpandableContent && (
                    <ChevronRight
                        size={14}
                        className={cn(
                            "shrink-0 text-[#223876] opacity-50 transition-transform duration-200",
                            expanded && "rotate-90"
                        )}
                    />
                )}
            </div>

            {expanded && (
                <ToolRowExpanded step={step} truncatedQuery={truncatedQuery} />
            )}
        </div>
    );
};

// ---------------------------------------------------------------------------
// ProcessingRow
// ---------------------------------------------------------------------------

const ProcessingRow: FC<{
    step: ProcessingStep;
    isLast?: boolean;
}> = ({ step, isLast }) => {
    const isInProgress = step.status === "in_progress";
    const isError = step.status === "error";
    const elapsedTime = useElapsedTime(isInProgress);
    const displayDuration = formatStepDuration(
        step.status,
        step.durationMs,
        elapsedTime
    );

    const getDisplayName = () => {
        if (
            (step.name === "api_error" || step.name === "api_retry") &&
            step.summaryText
        ) {
            return step.summaryText;
        }
        if (step.name === "verify_response" && isError) {
            return (
                step.summaryText ||
                "Detected inaccuracy; correcting response..."
            );
        }

        const names: Record<string, { active: string; complete: string }> = {
            validate_response: {
                active: "Validating response",
                complete: "Response validated",
            },
            verify_response: {
                active: "Verifying response",
                complete: "Response verified",
            },
            skill_loading: {
                active: "Reviewing skills...",
                complete: step.summaryText || "Reviewed skills",
            },
            api_retry: {
                active: "Retrying request...",
                complete: "Request succeeded",
            },
        };

        const entry = names[step.name];
        if (entry)
            return step.status === "complete" ? entry.complete : entry.active;
        return step.name;
    };

    // Retries in flight render with a yellow warning treatment, distinct from
    // both neutral "thinking" rows and the red ``api_error`` terminal state.
    const isRetryInProgress = step.name === "api_retry" && isInProgress;

    const displayStatus: StepStatus =
        step.name === "verify_response" && isError ? "complete" : step.status;
    const displayAsError = isError && step.name !== "verify_response";

    // Shared accent color class for icon + label so they can't drift apart.
    const accentColorClass = displayAsError
        ? "text-destructive"
        : isRetryInProgress
          ? "text-amber-800"
          : null;

    const getIcon = () => {
        if (step.name === "api_error")
            return <AlertCircle size={14} className="text-destructive" />;
        if (isRetryInProgress)
            return <AlertTriangle size={14} className="text-amber-800" />;
        if (step.name === "skill_loading") return <Wand2 size={14} />;
        if (step.name === "verify_response") return <ShieldCheck size={14} />;
        if (isError)
            return <AlertCircle size={14} className="text-destructive" />;
        return <ShieldCheck size={14} />;
    };

    return (
        <div
            className={cn(
                "animate-in fade-in slide-in-from-top-1 flex items-center gap-2 rounded-md px-2 py-1.5 duration-200",
                isRetryInProgress && "border-l-4 border-amber-400 bg-amber-100",
                !isLast && "mb-3"
            )}
        >
            <StatusIndicator status={displayStatus} />

            <span
                className={cn(
                    "flex",
                    isRetryInProgress ? "opacity-100" : "opacity-70",
                    accentColorClass ?? "text-[#223876]"
                )}
            >
                {getIcon()}
            </span>

            <span
                className={cn(
                    "text-[0.8125rem] font-medium",
                    accentColorClass ?? "text-foreground"
                )}
            >
                {getDisplayName()}
            </span>

            {displayDuration && (
                <span className="text-muted-foreground ml-1 text-xs tabular-nums">
                    {displayDuration}s
                </span>
            )}
        </div>
    );
};

// ---------------------------------------------------------------------------
// ChainOfThought
// ---------------------------------------------------------------------------

export const ChainOfThought: FC<ChainOfThoughtProps> = ({
    steps,
    isStreaming = false,
    defaultOpen = false,
}) => {
    const [expanded, setExpanded] = useState(defaultOpen);

    // Single pass over steps for summary-level flags
    let hasInProgress = false;
    let hasRealErrors = false;
    let hasInterrupted = false;
    let totalDurationMs = 0;
    for (const s of steps) {
        if (s.status === "in_progress") hasInProgress = true;
        if (s.status === "interrupted") hasInterrupted = true;
        if (
            s.status === "error" &&
            s.type === "processing" &&
            s.name !== "verify_response" &&
            s.name !== "skill_loading"
        ) {
            hasRealErrors = true;
        }
        totalDurationMs += s.durationMs || 0;
    }

    // A stopped turn (stream error or user abort) leaves its last in-flight
    // step "interrupted". Once streaming has ended, reflect the stop rather
    // than a false "complete" — the error banner carries the specifics.
    const isInterrupted = hasInterrupted && !hasInProgress && !isStreaming;
    const isComplete = !hasInProgress && !isStreaming && !hasInterrupted;

    // The header shows a total time only once the chain has completed — the
    // summed block durations (the only measure available on reload, since
    // nothing streamed). While the chain is still working no running clock is
    // shown in the header; the per-row timers carry that live feedback.
    const displayTotalTime =
        isComplete && totalDurationMs > 0
            ? (totalDurationMs / 1000).toFixed(1)
            : null;

    if (steps.length === 0) return null;

    // When a retry is in flight, append a short hint to whatever phase
    // summary is showing. The yellow row inside the dropdown is invisible
    // when collapsed, so without this hint a 35s retry looks like the chat
    // simply hung. Escalation is keyed off the row's own summaryText
    // ("unusual" only appears in the final-attempt copy), so the parent
    // line tracks calm → escalated → cleared without an extra signal.
    let retrySuffix: string | null = null;
    for (const s of steps) {
        if (
            s.type === "processing" &&
            s.name === "api_retry" &&
            s.status === "in_progress"
        ) {
            retrySuffix = (s.summaryText || "").includes("unusual")
                ? "still working, this is unusual"
                : "taking longer than expected";
            break;
        }
    }

    const withRetry = (base: string) =>
        retrySuffix ? `${base.replace(/\.\.\.$/, "")} — ${retrySuffix}` : base;

    const getSummary = () => {
        for (const s of steps) {
            if (s.type === "processing") {
                if (s.name === "skill_loading" && s.status === "in_progress")
                    return withRetry("Reviewing skills...");
                if (s.name === "verify_response" && s.status === "in_progress")
                    return withRetry("Verifying response...");
            }
        }

        if (isStreaming || hasInProgress) {
            if (
                steps.some(
                    (s) => s.type === "tool" && s.status === "in_progress"
                )
            )
                return withRetry("Searching documents...");
            if (
                steps.some(
                    (s) => s.type === "thinking" && s.status === "in_progress"
                )
            )
                return withRetry("Analyzing your question...");
            return withRetry("Thinking...");
        }

        if (hasRealErrors) {
            const errorCount = steps.filter(
                (s) =>
                    s.status === "error" &&
                    !(
                        s.type === "processing" &&
                        (s.name === "verify_response" ||
                            s.name === "skill_loading")
                    )
            ).length;
            return errorCount === 1
                ? "An error occurred"
                : `${errorCount} errors occurred`;
        }

        if (isInterrupted) return "Interrupted";

        const searchSteps = steps.filter(
            (s): s is ToolStep =>
                s.type === "tool" && s.toolName === "document_search"
        );
        if (searchSteps.length > 0) {
            let totalResults = 0;
            for (const s of searchSteps) {
                const r = s.result as
                    | { total_results?: number; data?: unknown[] }
                    | undefined;
                totalResults +=
                    r?.total_results ||
                    (Array.isArray(r?.data) ? r.data.length : 0);
            }
            if (totalResults > 0)
                return `Analyzed ${totalResults} relevant passages`;
            return `Searched ${searchSteps.length === 1 ? "documents" : `${searchSteps.length} queries`}`;
        }

        return "Analysis complete";
    };

    return (
        <Collapsible
            open={expanded}
            onOpenChange={setExpanded}
            className="animate-in fade-in slide-in-from-top-1 mb-3 duration-200"
        >
            {/* Summary header, minimal text line like Claude.ai */}
            <CollapsibleTrigger className="inline-flex cursor-pointer items-center gap-1.5 rounded py-1 transition-opacity duration-200 hover:opacity-70">
                <ChevronRight
                    size={14}
                    className={cn(
                        "shrink-0 text-black/40 transition-transform duration-200",
                        expanded && "rotate-90"
                    )}
                />

                {/* Status icon */}
                <StatusIndicator
                    status={
                        hasRealErrors
                            ? "error"
                            : isInterrupted
                              ? "interrupted"
                              : isComplete
                                ? "complete"
                                : "in_progress"
                    }
                />

                {/* Summary text */}
                <span className="text-muted-foreground text-[0.8125rem]">
                    {getSummary()}
                </span>

                {/* Total time */}
                {displayTotalTime && (
                    <span className="text-muted-foreground/50 text-[0.8125rem] tabular-nums">
                        {displayTotalTime}s
                    </span>
                )}
            </CollapsibleTrigger>

            <CollapsibleContent keepMounted>
                <div className="mt-2 ml-3 border-l-2 border-[rgba(34,56,118,0.15)] pl-4">
                    {steps.map((step, index) => {
                        const isLast = index === steps.length - 1;
                        switch (step.type) {
                            case "thinking":
                                return (
                                    <ThinkingRow
                                        key={index}
                                        step={step}
                                        isLast={isLast}
                                    />
                                );
                            case "tool":
                                return (
                                    <ToolRow
                                        key={index}
                                        step={step}
                                        isLast={isLast}
                                    />
                                );
                            case "processing":
                                return (
                                    <ProcessingRow
                                        key={index}
                                        step={step}
                                        isLast={isLast}
                                    />
                                );
                        }
                    })}
                </div>
            </CollapsibleContent>
        </Collapsible>
    );
};
